import { useEffect, useState } from 'react';
import { UMButton, UMFormGroup, UMIcon, UMInput, UMSelect } from '../../urbanmap/_components';
import { djuCoefficient, fmt, isValidDju, stationOf } from './domain';
import { PC, UMR, caption, card } from './tokens';
import type { DjuSettings, WeatherStation } from './types';
import { Pill } from '../proposition-c/ui';

interface Props {
  value: DjuSettings;
  defaults: DjuSettings;
  stations: WeatherStation[];
  onChange: (next: DjuSettings) => void;
  onValidityChange?: (valid: boolean) => void;
}

function Marker({ on }: { on: boolean }) {
  return on ? <Pill tone="accent" icon="edit">modifié</Pill> : <span style={caption}>par défaut</span>;
}

/** Station météo + DJU de référence (base 18 °C) → coefficient de correction climatique. */
export function DjuForm({ value, defaults, stations, onChange, onValidityChange }: Props) {
  const [refText, setRefText] = useState(String(value.reference));
  const refNum = Number(refText.replace(',', '.'));
  const refValid = refText.trim() !== '' && isValidDju(refNum);
  const station = stationOf(value);
  const coef = djuCoefficient(value);
  const stationChanged = value.stationId !== defaults.stationId;
  const refChanged = value.reference !== defaults.reference;

  useEffect(() => { onValidityChange?.(refValid); }, [refValid, onValidityChange]);

  const setRef = (s: string) => {
    setRefText(s);
    const n = Number(s.replace(',', '.'));
    if (s.trim() !== '' && isValidDju(n)) onChange({ ...value, reference: n });
  };
  const reset = () => { setRefText(String(defaults.reference)); onChange({ ...defaults }); };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, fontFamily: UMR.font, fontSize: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <strong>Degrés-jours unifiés (DJU)</strong>
          <span style={caption}>base 18 °C · valeurs annuelles</span>
        </div>
        <UMButton variant="subtle" icon="restart_alt" disabled={!stationChanged && !refChanged && refValid} onClick={reset}>Réinitialiser</UMButton>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <UMFormGroup label={<span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>Station météo <Marker on={stationChanged} /></span>}>
          <UMSelect
            ariaLabel="Station météo"
            value={value.stationId}
            options={stations.map((s) => ({ value: s.id, label: s.name }))}
            onChange={(stationId) => onChange({ ...value, stationId })}
          />
        </UMFormGroup>
        <UMFormGroup label={<span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>DJU de référence <Marker on={refChanged} /></span>}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ flex: 1, outline: refValid ? undefined : `2px solid ${PC.dangerText}` }}>
              <UMInput type="number" min={500} value={refText} ariaLabel="DJU de référence" onChange={setRef} />
            </div>
          </div>
          {!refValid && <div style={{ color: PC.dangerText, fontSize: 12 }}>Valeur invalide : entre 500 et 5 000</div>}
        </UMFormGroup>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div style={card}>
          <div style={caption}>DJU de la station (lecture seule)</div>
          <div style={{ fontWeight: 700, fontSize: 18, lineHeight: '26px' }}>{fmt(station.dju)}</div>
          <div style={caption}>{station.name} · normale annuelle</div>
        </div>
        <div style={{ ...card, borderColor: PC.accentBorder, background: PC.accentBg }}>
          <div style={caption}>Coefficient de correction calculé</div>
          <div style={{ fontWeight: 700, fontSize: 18, lineHeight: '26px', color: PC.accentStrong }}>× {fmt(coef, 3)}</div>
          <div style={caption}>DJU station / DJU de référence = {fmt(station.dju)} / {fmt(value.reference)}</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, ...caption }}>
        <UMIcon name="info" size={14} color={UMR.textSecondary} />
        <span>
          Conso. corrigée = ratio × surface chauffée × coefficient. Stations et DJU : valeurs de démonstration ;
          formule de correction à confirmer par le métier.
        </span>
      </div>
    </div>
  );
}
